import { Fragment } from "react";
import { Link } from "react-router-dom";
import SEO from "@/components/SEO";
import Reveal from "@/components/Reveal";
import StarDivider from "@/components/StarDivider";
import AnimatedCounter from "@/components/AnimatedCounter";
import { SITE, HERO_VIDEO, HERO_POSTER, DONATE_BANNER_IMG } from "@/data/content";
import {
  UserSearch,
  HandHeart,
  Award,
  Users,
  Building2,
  GraduationCap,
  Landmark,
  Vote,
  ArrowRight,
  Star as StarIcon,
  Calendar,
  DollarSign,
  ScrollText,
} from "lucide-react";

const WHAT_WE_DO = [
  { icon: UserSearch, title: "Recruit", text: "We find and recruit veterans and military family members to run for office." },
  { icon: HandHeart, title: "Support", text: "We provide resources, training, and guidance to help our candidates succeed." },
  { icon: Award, title: "Endorse", text: "We endorse and promote strong leaders who have served." },
  { icon: Users, title: "Connect", text: "We build a statewide network of veterans, families, and supporters across Indiana." },
  { icon: Vote, title: "Elect", text: "We help veterans and military families win — from local office to statewide leadership." },
];

const PATH = [
  { icon: GraduationCap, title: "School Board", text: "Strong communities start with local leaders." },
  { icon: Building2, title: "Town Council", text: "Building leadership and trust at the local level." },
  { icon: Landmark, title: "State House", text: "Representing Hoosier values and fighting for Indiana families." },
  { icon: StarIcon, title: "Statewide Office", text: "Leading Indiana with experience, integrity, and service." },
];

export default function Home() {
  return (
    <>
      <SEO
        title="Veterans for Indiana | Electing Veterans and Military Families"
        description="Veterans for Indiana is a PAC dedicated to recruiting, endorsing, and electing veterans and military families to public office across Indiana — from school board to statewide office."
        path="/"
      />

      {/* HERO — full-bleed video background with text overlay */}
      <section className="relative overflow-hidden bg-navy-deep text-white" aria-labelledby="hero-title">
        {/* Background video with poster fallback */}
        <video
          className="absolute inset-0 h-full w-full object-cover"
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          poster={HERO_POSTER}
          aria-hidden="true"
        >
          <source src={HERO_VIDEO} type="video/mp4" />
        </video>
        {/* Solid navy left overlay (~50% width) + fade right for crisp text legibility */}
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(90deg, rgba(6,27,51,0.97) 0%, rgba(6,27,51,0.97) 35%, rgba(6,27,51,0.75) 55%, rgba(6,27,51,0.35) 80%, rgba(6,27,51,0.2) 100%)",
          }}
        />
        <div className="absolute inset-0 pattern-stars opacity-15" />
        {/* Bottom green accent line */}
        <div className="absolute left-0 right-0 bottom-0 h-1 bg-green-brand" />

        <div className="container-x relative py-20 lg:py-32 grid lg:grid-cols-2 gap-10 items-center">
          <div className="max-w-2xl">
            <Reveal>
              <p className="eyebrow text-green-brand">Veterans-led · Indiana-strong</p>
            </Reveal>
            <Reveal delay={1}>
              <h1 id="hero-title" className="h-display text-4xl sm:text-5xl lg:text-6xl mt-3 leading-[1.02] text-white" style={{ textShadow: "0 2px 16px rgba(6,27,51,0.85)" }}>
                Veterans Stepped Up to Serve Our Country.
                <span className="block text-green-brand mt-3" style={{ textShadow: "0 2px 16px rgba(6,27,51,0.85)" }}>Now We&apos;re Stepping Up to Serve Indiana.</span>
              </h1>
            </Reveal>
            <Reveal delay={2}>
              <span className="divider-green mt-6 inline-block" />
            </Reveal>
            <Reveal delay={2}>
              <p className="mt-6 max-w-xl text-base sm:text-lg leading-relaxed text-white/90">
                Veterans for Indiana is dedicated to electing veterans and military families to offices across the state — from school board to statewide office.
              </p>
            </Reveal>
            <Reveal delay={3}>
              <div className="mt-8 flex flex-wrap gap-3" data-testid="hero-cta-group">
                <a
                  href={SITE.donateUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-primary"
                  data-testid="hero-donate-cta"
                >
                  <DollarSign size={18} /> Donate
                </a>
                <Link to="/get-involved" className="btn btn-secondary" data-testid="hero-endorse-cta">
                  Apply for Endorsement
                </Link>
                <Link to="/get-involved" className="btn btn-ghost-light" data-testid="hero-join-cta">
                  Join Our Network
                </Link>
              </div>
            </Reveal>
          </div>

          {/* Right column: subtle accent decorations on video (no Indiana outline / badge per design update) */}
          <Reveal delay={2} className="hidden lg:block relative">
            <div className="relative h-[420px]" aria-hidden="true">
              {/* intentionally left empty — video carries the visual */}
            </div>
          </Reveal>
        </div>
      </section>

      {/* VETERANS IN ACTION — photo strip below hero */}
      <section className="bg-cream py-20" aria-labelledby="action-title">
        <div className="container-x">
          <Reveal className="text-center max-w-3xl mx-auto">
            <p className="eyebrow">On the Ground</p>
            <h2 id="action-title" className="h-display text-3xl sm:text-4xl text-navy mt-2">Veterans in Action</h2>
            <div className="mt-3 flex justify-center"><StarDivider /></div>
            <p className="mt-5 text-navy-deep/80 leading-relaxed">
              From neighborhood doorsteps to the Statehouse, our veterans and military families are showing up — for their candidates, their communities, and the future of Indiana.
            </p>
          </Reveal>

          <div className="mt-12 grid md:grid-cols-2 gap-6 lg:gap-8">
            <Reveal delay={1}>
              <figure className="card-lift relative rounded-2xl overflow-hidden shadow-md ring-1 ring-gray-200">
                <img
                  src="/assets/photos/canvassing.webp"
                  alt="Veterans for Indiana endorsed candidates and supporters canvassing in a Hoosier neighborhood"
                  loading="lazy"
                  className="w-full h-auto block transition-transform duration-500 hover:scale-[1.03]"
                />
              </figure>
            </Reveal>

            <Reveal delay={2}>
              <figure className="card-lift relative rounded-2xl overflow-hidden shadow-md ring-1 ring-gray-200">
                <img
                  src="/assets/photos/event.webp"
                  alt="Veterans for Indiana leaders at a campaign event with U.S. and Indiana state flags"
                  loading="lazy"
                  className="w-full h-auto block transition-transform duration-500 hover:scale-[1.03]"
                />
              </figure>
            </Reveal>
          </div>
        </div>
      </section>

      {/* WHAT WE DO */}
      <section className="bg-white py-20" aria-labelledby="wwd-title">
        <div className="container-x">
          <Reveal className="text-center">
            <h2 id="wwd-title" className="h-display text-3xl sm:text-4xl">What We Do</h2>
            <div className="mt-3 flex justify-center"><StarDivider /></div>
          </Reveal>
          <div className="mt-12 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-y-12 gap-x-6 lg:divide-x lg:divide-gray-200">
            {WHAT_WE_DO.map(({ icon: Icon, title, text }, i) => (
              <Reveal key={title} delay={(i % 5) + 1} className="text-center lg:px-4">
                <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-green-brand/10 text-green-brand">
                  <Icon size={28} />
                </div>
                <h3 className="font-display tracking-widest mt-4 text-navy text-lg uppercase">{title}</h3>
                <span className="mt-2 inline-block h-[2px] w-10 bg-green-brand" />
                <p className="mt-3 text-sm text-navy-deep/75 leading-relaxed max-w-[200px] mx-auto">{text}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* OUR IMPACT — Indiana flag background, larger numbers + icons */}
      <section className="relative text-white py-24 overflow-hidden" aria-labelledby="impact-title" style={{ backgroundColor: "#0A1F47" }}>
        {/* Indiana state flag as background */}
        <div
          className="absolute inset-0 opacity-30"
          style={{
            backgroundImage: "url('/assets/indiana-flag.svg')",
            backgroundSize: "auto 140%",
            backgroundPosition: "center",
            backgroundRepeat: "no-repeat",
          }}
          aria-hidden="true"
        />
        {/* Subtle gold radial behind the torch */}
        <div className="absolute inset-0" style={{ background: "radial-gradient(circle at 50% 50%, rgba(255,185,0,0.12), transparent 60%)" }} aria-hidden="true" />
        <div className="container-x relative">
          <Reveal className="text-center">
            <h2 id="impact-title" className="h-display text-4xl sm:text-5xl tracking-widest text-white">Our Impact</h2>
            <div className="mt-4 flex justify-center"><StarDivider color="#FFB900" /></div>
          </Reveal>
          <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-8 lg:divide-x lg:divide-white/20">
            <ImpactStat
              icon={Calendar}
              label="Since our start in"
              value={<AnimatedCounter end={2024} format={false} />}
              copy="Building a stronger future for Indiana through veteran leadership."
            />
            <ImpactStat
              icon={HandHeart}
              label="Endorsed and supported"
              value={<><AnimatedCounter end={16} /></>}
              copy="veteran and military family candidates across Indiana."
              withStars
            />
            <ImpactStat
              icon={DollarSign}
              label="Raised over"
              value={<AnimatedCounter end={30} prefix="$" suffix="K+" />}
              copy="$20K went directly to candidates."
            />
            <ImpactStat
              icon={ScrollText}
              label="Supported and advocated for"
              value={<span className="text-[#FFB900]">Veteran-Friendly Legislation</span>}
              valueClass="text-2xl sm:text-3xl lg:text-4xl"
              copy="Fighting for policies that support those who served."
            />
          </div>
        </div>
      </section>

      {/* FROM SCHOOL BOARD TO STATEWIDE */}
      <section className="bg-white py-20" aria-labelledby="path-title">
        <div className="container-x">
          <Reveal className="text-center">
            <h2 id="path-title" className="h-display text-3xl sm:text-4xl">From School Board to Statewide Office</h2>
            <div className="mt-3 flex justify-center"><StarDivider /></div>
          </Reveal>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-7 gap-6 items-start">
            {PATH.map(({ icon: Icon, title, text }, i) => (
              <Fragment key={title}>
                <Reveal delay={(i + 1)} className="lg:col-span-1 text-center">
                  <div className={`mx-auto flex h-20 w-20 items-center justify-center rounded-full ${i % 2 === 0 ? "bg-navy text-white" : "bg-green-brand text-white"}`}>
                    <Icon size={32} />
                  </div>
                  <h3 className="font-display tracking-widest text-navy uppercase mt-4 text-sm">{title}</h3>
                  <p className="mt-2 text-sm text-navy-deep/75 max-w-[210px] mx-auto leading-relaxed">{text}</p>
                </Reveal>
                {i < PATH.length - 1 && (
                  <div className="hidden lg:flex items-center justify-center pt-7" aria-hidden="true">
                    <ArrowRight className="text-green-brand" size={32} />
                  </div>
                )}
              </Fragment>
            ))}
          </div>
          <Reveal className="text-center mt-12">
            <p className="font-display tracking-wide text-navy text-lg sm:text-xl">
              Leadership starts locally. We help veterans keep serving at every level.
            </p>
            <span className="divider-green mt-4 inline-block" />
          </Reveal>
        </div>
      </section>

      {/* DONATION BANNER — navy with flag/barn sunset on right */}
      <section className="relative bg-navy-deep text-white overflow-hidden">
        <div className="absolute inset-0 pattern-stars opacity-30" aria-hidden="true" />
        {/* Right half: flag + barn sunset photo */}
        <div className="absolute right-0 top-0 bottom-0 w-full md:w-1/2" aria-hidden="true">
          <div
            className="absolute inset-0"
            style={{
              backgroundImage: `linear-gradient(90deg, rgba(11,42,74,1) 0%, rgba(11,42,74,0.6) 30%, rgba(11,42,74,0) 100%), url('${DONATE_BANNER_IMG}')`,
              backgroundSize: "cover",
              backgroundPosition: "center",
            }}
          />
        </div>
        <div className="container-x relative py-16 lg:py-20 grid lg:grid-cols-2 gap-8 items-center">
          <Reveal>
            <h2 className="h-display text-3xl sm:text-4xl text-white">
              Service Didn&apos;t End With the Uniform.
              <span className="block text-green-brand mt-1">Join the Mission.</span>
            </h2>
          </Reveal>
          <Reveal delay={1} className="max-w-md">
            <p className="text-white/90 leading-relaxed">
              Your support helps veterans and military families continue their mission of leadership in Indiana.
            </p>
            <a
              href={SITE.donateUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-primary mt-6"
              data-testid="donate-banner-cta"
            >
              <DollarSign size={18} /> Donate Today
            </a>
          </Reveal>
        </div>
      </section>
    </>
  );
}

function ImpactStat({ icon: Icon, label, value, copy, valueClass, withStars }) {
  return (
    <Reveal className="text-center lg:px-8">
      <div className="mx-auto mb-5 inline-flex h-20 w-20 items-center justify-center rounded-full bg-white/10 ring-2 ring-[#FFB900]/70 text-[#FFB900] backdrop-blur-sm">
        <Icon size={36} strokeWidth={2} />
      </div>
      <p className="font-display uppercase tracking-[0.18em] text-sm text-white/85">{label}</p>
      <div className={`mt-3 font-display text-6xl sm:text-7xl font-bold text-white leading-none ${valueClass || ""}`}>
        {withStars ? (
          <span className="inline-flex items-center gap-3">
            <StarIcon size={22} className="fill-[#FFB900] text-[#FFB900]" />
            {value}
            <StarIcon size={22} className="fill-[#FFB900] text-[#FFB900]" />
          </span>
        ) : value}
      </div>
      <p className="mt-4 text-base text-white/85 leading-relaxed max-w-[260px] mx-auto">{copy}</p>
    </Reveal>
  );
}
